import React from "react";
import { Link } from "react-router-dom";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import styles from "./MobileCategoryTree.module.css";

interface Category {
  id: number;
  name: string;
  url: string;
  children: Category[];
}

interface CategoryItemProps {
  category: Category;
  onSelect: (category: Category) => void;
  closeMenu: () => void;
}

const CategoryItem: React.FC<CategoryItemProps> = ({
  category,
  onSelect,
  closeMenu,
}) => {
  const handleClick = (event: React.MouseEvent) => {
    event.stopPropagation();

    // If the category has children, open the next "page"
    if (category.children.length > 0) {
      onSelect(category);
    } else {
      // Close menu when clicking on a link to a category with no children
      closeMenu();
    }
  };

  return (
    <div className={styles.categoryCard} onClick={handleClick}>
      <div className={styles.cardContent}>
        {/* If category has no children, wrap name in <Link> for routing */}
        {category.children.length === 0 ? (
          <Link to={`/c/${category.url}`} className={styles.categoryName}>
            {category.name}
          </Link>
        ) : (
          <span className={styles.categoryName}>{category.name}</span>
        )}

        {/* Show ArrowForwardIosIcon if the category has children */}
        {category.children.length > 0 && (
          <ArrowForwardIosIcon
            className={`${styles.icon} ${styles.iconRight}`}
          />
        )}
      </div>
    </div>
  );
};

interface CategoryScreenProps {
  category: Category;
  onBack: () => void;
  onSelect: (category: Category) => void;
  level: number;
  closeMenu: () => void;
}

const CategoryScreen: React.FC<CategoryScreenProps> = ({
  category,
  onBack,
  onSelect,
  level,
  closeMenu,
}) => (
  <div className={styles.categoryScreen}>
    <div className={styles.backButtonContainer}>
      <button className={styles.backButton} onClick={onBack}>
        <ArrowBackIosNewIcon className={styles.backIcon} />
        {level === 2 ? "Menú Principal" : "Volver"}
      </button>
    </div>

    {category.children.length > 0 && (
      <div className={styles.categoryHeader}>
        <h2 title={category.name}>{category.name}</h2>
        {/* Close menu when clicking "Ver todo" */}
        <Link
          to={`/c/${category.url}`}
          className={styles.viewAllLink}
          onClick={closeMenu}
        >
          Ver todo
        </Link>
      </div>
    )}

    <div className={styles.subcategories}>
      {category.children.map((child) => (
        <React.Fragment key={child.id}>
          <CategoryItem
            category={child}
            onSelect={onSelect}
            closeMenu={closeMenu}
          />
          <hr className={styles.divider} />
        </React.Fragment>
      ))}

      {category.children.length === 0 && (
        <div className={styles.noSubcategories}>
          <p>No hay subcategorías disponibles</p>
          <Link
            to={`/c/${category.url}`}
            className={`buttonMain ${styles.browseButton}`}
            onClick={closeMenu}
          >
            Explorar {category.name}
          </Link>
        </div>
      )}
    </div>
  </div>
);

const MobileCategoryTree: React.FC<{
  category: Category;
  onBack: () => void;
  onSelect: (category: Category) => void;
  currentLevel: number;
  closeMenu: () => void;
}> = ({ category, onBack, onSelect, currentLevel, closeMenu }) => (
  <div className={styles.categoryList}>
    <CategoryScreen
      category={category}
      onBack={onBack}
      onSelect={onSelect}
      level={currentLevel}
      closeMenu={closeMenu}
    />
  </div>
);

export default MobileCategoryTree;
